import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, Panel, Field, Empty, NeedClinic, StatusBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDateTime, useLoad } from "@/lib/api";
import { can, DEPARTMENTS } from "@/lib/constants";

const deptsFor = (c) => Object.keys(DEPARTMENTS).filter((k) => (k !== "dental" || c.modules?.dental) && (k !== "procedure" || c.modules?.procedures));

function NewVisit({ clinic, onClose, onDone }) {
  const p = { clinic_id: clinic.id };
  const [patients] = useLoad("/patients", p);
  const [doctors] = useLoad("/staff/doctors", p);
  const [f, setF] = useState({ patient_id: "", department: "general", doctor_id: "", complaint: "" });
  const save = async () => {
    if (!f.patient_id) return toast.error("Select a patient");
    try { await api.post("/visits", f, { params: p }); toast.success("Added to queue"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>New consultation</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Field label="Patient"><Select value={f.patient_id} onValueChange={(v) => setF({ ...f, patient_id: v })}>
            <SelectTrigger data-testid="visit-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
            <SelectContent>{(patients || []).map((x) => <SelectItem key={x.id} value={x.id}>{x.patient_no} · {x.first_name} {x.last_name}</SelectItem>)}</SelectContent></Select></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Department"><Select value={f.department} onValueChange={(v) => setF({ ...f, department: v })}>
              <SelectTrigger data-testid="visit-department-select"><SelectValue /></SelectTrigger>
              <SelectContent>{deptsFor(clinic).map((k) => <SelectItem key={k} value={k}>{DEPARTMENTS[k]}</SelectItem>)}</SelectContent></Select></Field>
            <Field label="Doctor / Dentist"><Select value={f.doctor_id || undefined} onValueChange={(v) => setF({ ...f, doctor_id: v })}>
              <SelectTrigger data-testid="visit-doctor-select"><SelectValue placeholder="Any" /></SelectTrigger>
              <SelectContent>{(doctors || []).map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select></Field>
          </div>
          <Field label="Presenting complaint"><Textarea value={f.complaint} onChange={(e) => setF({ ...f, complaint: e.target.value })} data-testid="visit-complaint-input" /></Field>
        </div>
        <DialogFooter><Button onClick={save} data-testid="visit-form-submit-button">Add to queue</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateVisit({ v, onClose, onDone }) {
  const [f, setF] = useState({ vitals: v.vitals, diagnosis: v.diagnosis, notes: v.notes, status: v.status });
  const save = async () => {
    try { await api.put(`/visits/${v.id}`, f); toast.success("Consultation updated"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader><DialogTitle>{v.patient_name} · {DEPARTMENTS[v.department]}</DialogTitle></DialogHeader>
        <div className="grid gap-4">
          <Field label="Status"><Select value={f.status} onValueChange={(s) => setF({ ...f, status: s })}>
            <SelectTrigger data-testid="visit-status-select"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="waiting">Waiting</SelectItem><SelectItem value="in_consultation">In consultation</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent></Select></Field>
          <Field label="Vitals"><Input value={f.vitals} onChange={(e) => setF({ ...f, vitals: e.target.value })} placeholder="BP, temp, pulse, weight" data-testid="visit-vitals-input" /></Field>
          <Field label="Diagnosis"><Input value={f.diagnosis} onChange={(e) => setF({ ...f, diagnosis: e.target.value })} data-testid="visit-diagnosis-input" /></Field>
          <Field label="Clinical notes / plan"><Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="visit-notes-input" /></Field>
        </div>
        <DialogFooter><Button onClick={save} data-testid="visit-update-submit-button">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Visits() {
  const { user, activeClinic, activeClinicId } = useAuth();
  const [status, setStatus] = useState("all");
  const [rows, reload] = useLoad(activeClinicId ? "/visits" : null, { clinic_id: activeClinicId, status: status === "all" ? "" : status });
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  if (!activeClinic) return <NeedClinic isCeo={user.role === "ceo"} />;
  const done = () => { setAdding(false); setEditing(null); reload(); };
  return (
    <>
      <PageHeader eyebrow={activeClinic.name} title="Consultations" subtitle="Outpatient queue across General OPD, procedures, dental, lab and pharmacy.">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 rounded-full bg-white" data-testid="visit-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="waiting">Waiting</SelectItem><SelectItem value="in_consultation">In consultation</SelectItem><SelectItem value="completed">Completed</SelectItem></SelectContent>
        </Select>
        {can(user, "createPatient") && <Button className="rounded-full" onClick={() => setAdding(true)} data-testid="add-visit-button"><Plus className="mr-2 h-4 w-4" />New consultation</Button>}
      </PageHeader>
      <Panel testId="visits-table">
        {!rows?.length ? <Empty text="No consultations." /> : (
          <div className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Patient</TableHead><TableHead>Department</TableHead><TableHead>Doctor</TableHead><TableHead>Diagnosis</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>{rows.map((v) => (
              <TableRow key={v.id} data-testid={`visit-row-${v.id}`}>
                <TableCell className="text-sm text-slate-500 whitespace-nowrap">{fmtDateTime(v.created_at)}</TableCell>
                <TableCell><p className="font-semibold">{v.patient_name}</p><p className="font-mono text-xs text-emerald-700">{v.patient_no}</p></TableCell>
                <TableCell>{DEPARTMENTS[v.department]}</TableCell>
                <TableCell>{v.doctor_name || "—"}</TableCell>
                <TableCell className="max-w-[200px] truncate">{v.diagnosis || "—"}</TableCell>
                <TableCell><StatusBadge status={v.status} /></TableCell>
                <TableCell>{can(user, "updateVisit") && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditing(v)} data-testid={`visit-open-${v.id}`}>Open</Button>}</TableCell>
              </TableRow>))}</TableBody>
          </Table></div>)}
      </Panel>
      {adding && <NewVisit clinic={activeClinic} onClose={() => setAdding(false)} onDone={done} />}
      {editing && <UpdateVisit v={editing} onClose={() => setEditing(null)} onDone={done} />}
    </>
  );
}
